import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, 
  Settings, 
  Shield, 
  RefreshCw, 
  CheckCircle, 
  XCircle,
  Eye,
  EyeOff,
  CalendarIcon,
  Users,
  Trash2,
  Search,
  AlertTriangle
} from 'lucide-react';
import { format, startOfYear } from 'date-fns';
import { ru } from 'date-fns/locale';
import apiClient from '@/api/client';

/**
 * BitrixIntegrationPage — Stage 7B-FIX v3
 * 
 * Настройка интеграции Bitrix24:
 * - Webhook URL (encrypted)
 * - Whitelist сотрудников (bitrix_user_id)
 * - Дата начала импорта
 * - Auto-detect поля клиента
 * - Очистка и пересинхронизация
 */
const BitrixIntegrationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const userRole = user?.role?.toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'finance_admin';
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Settings from API
  const [settings, setSettings] = useState({
    enabled: false,
    base_url: '',
    masked_webhook: '',
    polling_interval_min: 15,
    import_start_date: null,
    bitrix_whitelist_user_ids: [],
    bitrix_client_field_code: null,
    last_sync_at: null,
    last_sync_status: null,
    last_sync_message: null,
    sync_stats: null,
    configured: false,
  });
  
  // Form data
  const [formData, setFormData] = useState({
    enabled: false,
    base_url: '',
    webhook_url: '',
    polling_interval_min: 15,
    import_start_date: startOfYear(new Date()),
    whitelist_input: '',  // Raw input (comma/newline separated)
    bitrix_client_field_code: '',
  });
  
  // Whitelist check result
  const [checkResult, setCheckResult] = useState(null);
  
  // Test result
  const [testResult, setTestResult] = useState(null);

  // Date presets
  const datePresets = [
    { id: 'year', label: 'С начала года', getDate: () => startOfYear(new Date()) },
    { id: '2024', label: 'С 01.01.2024', getDate: () => new Date(2024, 0, 1) },
    { id: 'custom', label: 'Своя дата', getDate: null },
  ];
  const [selectedPreset, setSelectedPreset] = useState('year');

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/integrations/bitrix');
      setSettings(response.data);
      
      // Parse date from API
      let importDate = startOfYear(new Date());
      if (response.data.import_start_date) {
        importDate = new Date(response.data.import_start_date);
      }
      
      // Detect preset
      const today = new Date();
      const yearStart = startOfYear(today);
      const jan2024 = new Date(2024, 0, 1);
      
      let preset = 'custom';
      if (isSameDate(importDate, yearStart)) preset = 'year';
      else if (isSameDate(importDate, jan2024)) preset = '2024';
      
      setSelectedPreset(preset);
      
      // Format whitelist for display
      const whitelistStr = (response.data.bitrix_whitelist_user_ids || []).join(', ');
      
      setFormData({
        enabled: response.data.enabled,
        base_url: response.data.base_url || '',
        webhook_url: '',
        polling_interval_min: response.data.polling_interval_min || 15,
        import_start_date: importDate,
        whitelist_input: whitelistStr,
        bitrix_client_field_code: response.data.bitrix_client_field_code || '',
      });
    } catch (error) {
      showError(error, 'Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadSettings();
  }, [isAdmin, navigate, loadSettings]);

  const isSameDate = (d1, d2) => {
    return d1.toDateString() === d2.toDateString();
  };

  const handlePresetChange = (presetId) => {
    setSelectedPreset(presetId);
    const preset = datePresets.find(p => p.id === presetId);
    if (preset?.getDate) {
      setFormData({ ...formData, import_start_date: preset.getDate() });
    }
  };

  const handleDateChange = (date) => {
    if (date && date > new Date()) {
      showError(null, 'Дата не может быть в будущем');
      return;
    }
    setFormData({ ...formData, import_start_date: date });
    setSelectedPreset('custom');
    setCalendarOpen(false);
  };

  /**
   * Парсинг whitelist из текстового поля.
   * Поддерживает: запятые, переносы строк, пробелы.
   */
  const parseWhitelist = (input) => {
    if (!input || !input.trim()) return { ids: [], errors: [] };

    // Разбиваем по запятым, переносам строк, пробелам
    const parts = input.split(/[\s,\n]+/).filter(Boolean);

    const ids = [];
    const errors = [];

    parts.forEach(part => {
      const num = parseInt(part.trim(), 10);
      if (isNaN(num) || num <= 0) {
        errors.push(part);
      } else {
        ids.push(num);
      }
    });

    return { ids, errors };
  };

  const handleSave = async () => {
    if (!formData.webhook_url && !settings.configured) {
      showError(null, 'Введите Webhook URL');
      return;
    }
    
    if (!formData.import_start_date) {
      showError(null, 'Выберите дату начала импорта');
      return;
    }
    
    // Парсим whitelist
    const { ids: whitelistIds, errors } = parseWhitelist(formData.whitelist_input);
    
    if (errors.length > 0) {
      showError(null, `Некорректные ID: ${errors.join(', ')}`);
      return;
    }
    
    if (whitelistIds.length === 0) {
      showError(null, 'Укажите хотя бы один Bitrix User ID для синхронизации');
      return;
    }
    
    try {
      setSaving(true);
      
      let response;
      
      // Если webhook не изменился и уже настроен — используем PUT
      if (!formData.webhook_url && settings.configured) {
        const updatePayload = {
          enabled: formData.enabled,
          polling_interval_min: formData.polling_interval_min,
          import_start_date: format(formData.import_start_date, 'yyyy-MM-dd'),
          bitrix_whitelist_user_ids: whitelistIds,
          bitrix_client_field_code: formData.bitrix_client_field_code || null,
        };
        
        response = await apiClient.put('/integrations/bitrix', updatePayload);
      } else {
        // Создаём/обновляем с новым webhook — используем POST
        const createPayload = {
          enabled: formData.enabled,
          base_url: formData.base_url || extractBaseUrl(formData.webhook_url),
          webhook_url: formData.webhook_url,
          polling_interval_min: formData.polling_interval_min,
          import_start_date: format(formData.import_start_date, 'yyyy-MM-dd'),
          bitrix_whitelist_user_ids: whitelistIds,
          bitrix_client_field_code: formData.bitrix_client_field_code || null,
        };
        
        response = await apiClient.post('/integrations/bitrix', createPayload);
      }
      
      setFormData(prev => ({ ...prev, webhook_url: '' }));
      setSettings(response.data);
      
      showSuccess('Настройки Bitrix24 сохранены');
      setTestResult(null);
      setCheckResult(null);
      
    } catch (error) {
      showError(error, 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await apiClient.post('/integrations/bitrix/test');
      setTestResult(response.data);
      
      if (response.data.ok) {
        showSuccess(`Подключено к ${response.data.portal}`);
      } else {
        showError(null, response.data.error || 'Ошибка подключения');
      }
      
    } catch (error) {
      showError(error, 'Ошибка тестирования');
      setTestResult({ ok: false, error: 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  /**
   * Проверить whitelist — показать сколько задач будет синхронизировано.
   */
  const handleCheckWhitelist = async () => {
    const { ids: whitelistIds, errors } = parseWhitelist(formData.whitelist_input);
    
    if (errors.length > 0) {
      showError(null, `Некорректные ID: ${errors.join(', ')}`);
      return;
    }
    
    if (whitelistIds.length === 0) {
      showError(null, 'Укажите хотя бы один Bitrix User ID');
      return;
    }
    
    try {
      setChecking(true);
      setCheckResult(null);
      
      const response = await apiClient.post('/integrations/bitrix/check-whitelist', whitelistIds);
      setCheckResult(response.data);
      
      if (response.data.ok) {
        showSuccess(`Найдено ${response.data.tasks_count} задач для ${response.data.users_count} сотрудников`);
      } else {
        showError(null, response.data.error);
      }
      
    } catch (error) {
      showError(error, 'Ошибка проверки whitelist');
    } finally {
      setChecking(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await apiClient.post('/integrations/bitrix/sync');
      
      const stats = response.data.stats || {};
      showSuccess(`Синхронизация завершена: ${stats.created || 0} создано, ${stats.updated || 0} обновлено`);
      
      await loadSettings();
      
    } catch (error) {
      showError(error, 'Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Очистить все проекты из Bitrix.
   */
  const handleClearProjects = async () => {
    if (!window.confirm('Удалить ВСЕ проекты, импортированные из Bitrix?\n\nЭто действие нельзя отменить. После удаления можно выполнить повторную синхронизацию.')) {
      return;
    }
    
    try {
      setClearing(true);
      const response = await apiClient.delete('/integrations/bitrix/projects');
      
      showSuccess(`Удалено ${response.data.deleted_count} проектов`);
      
    } catch (error) {
      showError(error, 'Ошибка очистки проектов');
    } finally {
      setClearing(false);
    }
  };

  const extractBaseUrl = (webhookUrl) => {
    if (!webhookUrl) return '';
    try {
      const url = new URL(webhookUrl);
      return `${url.protocol}//${url.host}`;
    } catch {
      return '';
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bitrix-integration-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Назад
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Settings className="w-6 h-6" />
                Интеграция Bitrix24
              </h1>
              <p className="text-sm text-gray-600">Настройка синхронизации проектов (только для Admin)</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Security Notice */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Безопасность</p>
                <p>Webhook URL хранится в зашифрованном виде и никогда не отображается после сохранения.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Статус подключения</span>
              {settings.configured ? (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" /> Настроено
                </Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-800">
                  <XCircle className="w-3 h-3 mr-1" /> Не настроено
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.configured && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Портал:</span>
                  <span className="ml-2 font-medium">{settings.base_url?.replace('https://', '')}</span>
                </div>
                <div>
                  <span className="text-gray-500">Webhook:</span>
                  <span className="ml-2 font-mono">{settings.masked_webhook}</span>
                </div>
                <div>
                  <span className="text-gray-500">Whitelist:</span>
                  <span className="ml-2">{settings.bitrix_whitelist_user_ids?.length || 0} сотрудников</span>
                </div>
                <div>
                  <span className="text-gray-500">Поле клиента:</span>
                  <span className="ml-2 font-mono text-xs">
                    {settings.bitrix_client_field_code || 'auto-detect'}
                  </span>
                </div>
                {settings.last_sync_at && (
                  <>
                    <div>
                      <span className="text-gray-500">Последняя синхронизация:</span>
                      <span className="ml-2">{new Date(settings.last_sync_at).toLocaleString('ru')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Статус:</span>
                      <span className={`ml-2 ${settings.last_sync_status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {settings.last_sync_message}
                      </span>
                    </div>
                  </>
                )}
                {settings.sync_stats && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Статистика:</span>
                    <span className="ml-2">
                      Найдено: {settings.sync_stats.tasks_found}, 
                      создано: {settings.sync_stats.created}, 
                      обновлено: {settings.sync_stats.updated}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Test Result */}
            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {testResult.ok ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Подключено к {testResult.portal} (User: {testResult.user_name})</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    <span>{testResult.error}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Action Buttons */}
            {settings.configured && (
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing}
                  data-testid="test-connection-btn"
                >
                  {testing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Тест подключения
                </Button>
                <Button
                  onClick={handleSync}
                  disabled={syncing || !settings.enabled}
                  data-testid="sync-btn"
                >
                  {syncing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Синхронизировать
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleClearProjects}
                  disabled={clearing}
                  data-testid="clear-projects-btn"
                >
                  {clearing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Очистить проекты
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Whitelist Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Whitelist сотрудников
            </CardTitle>
            <CardDescription>
              Синхронизируются только задачи, где ответственный (RESPONSIBLE_ID) входит в этот список.
              Укажите Bitrix User ID через запятую или с новой строки.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="whitelist">Bitrix User IDs</Label>
              <Textarea
                id="whitelist"
                value={formData.whitelist_input}
                onChange={(e) => setFormData({ ...formData, whitelist_input: e.target.value })}
                placeholder="16, 2471, 4545, 5678&#10;или по одному ID на строку"
                rows={4}
                className="font-mono text-sm"
                data-testid="whitelist-input"
              />
              <p className="text-xs text-gray-500">
                Найти User ID: откройте профиль сотрудника в Bitrix → ID в URL (например, /company/personal/user/2471/)
              </p>
            </div>
            
            <Button
              variant="outline"
              onClick={handleCheckWhitelist}
              disabled={checking || !settings.configured}
              data-testid="check-whitelist-btn"
            >
              {checking ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Проверить (preview)
            </Button>
            
            {/* Check Result */}
            {checkResult && (
              <div className={`p-4 rounded-lg border ${checkResult.ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                {checkResult.ok ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">
                        Найдено {checkResult.tasks_count} задач для {checkResult.users_count} сотрудников
                      </span>
                    </div>
                    
                    {checkResult.users && checkResult.users.length > 0 && (
                      <div className="text-sm">
                        <p className="text-gray-600 mb-1">Сотрудники:</p>
                        <ul className="list-disc list-inside text-gray-700">
                          {checkResult.users.map(u => (
                            <li key={u.id}>
                              {u.name} <span className="text-gray-400">(ID: {u.id})</span>
                              {u.position && <span className="text-gray-500"> — {u.position}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {checkResult.detected_client_field && (
                      <div className="text-sm">
                        <p className="text-gray-600">
                          Поле клиента (auto-detect): <code className="bg-white px-1 rounded">{checkResult.detected_client_field}</code>
                        </p>
                        {checkResult.sample_client_values?.length > 0 && (
                          <p className="text-gray-500">
                            Примеры значений: {checkResult.sample_client_values.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-800">
                    <XCircle className="w-4 h-4" />
                    <span>{checkResult.error}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle>Настройки интеграции</CardTitle>
            <CardDescription>
              {settings.configured 
                ? 'Для изменения webhook введите новый URL' 
                : 'Введите данные для подключения к Bitrix24'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enabled">Включить интеграцию</Label>
                <p className="text-sm text-gray-500">Автоматическая синхронизация задач</p>
              </div>
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                disabled={!settings.configured && !formData.webhook_url}
                data-testid="enabled-switch"
              />
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <Label htmlFor="webhook_url">
                Webhook URL
                {settings.configured && <span className="text-gray-400 ml-2">(оставьте пустым, чтобы не менять)</span>}
              </Label>
              <div className="relative">
                <Input
                  id="webhook_url"
                  type={showWebhook ? 'text' : 'password'}
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://lgx.bitrix24.kz/rest/1/xxxxxxxxxxxx/"
                  autoComplete="off"
                  data-testid="webhook-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowWebhook(!showWebhook)}
                >
                  {showWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Формат: https://ваш-домен.bitrix24.kz/rest/USER_ID/TOKEN/
              </p>
            </div>

            {/* Client Field Override */}
            <div className="space-y-2">
              <Label htmlFor="client_field">
                Поле клиента (UF_*)
                <span className="text-gray-400 ml-2">опционально</span>
              </Label>
              <Input
                id="client_field"
                value={formData.bitrix_client_field_code}
                onChange={(e) => setFormData({ ...formData, bitrix_client_field_code: e.target.value })}
                placeholder="UF_AUTO_123456 (оставьте пустым для auto-detect)"
                className="font-mono"
                data-testid="client-field-input"
              />
              <p className="text-xs text-gray-500">
                Кастомное поле задачи с названием клиента (ГП/ВС/ВАП/МФ). Если не указано — определится автоматически.
              </p>
            </div>

            {/* Polling Interval */}
            <div className="space-y-2">
              <Label htmlFor="polling_interval">Интервал опроса (мин)</Label>
              <Input
                id="polling_interval"
                type="number"
                min="3"
                max="60"
                value={formData.polling_interval_min}
                onChange={(e) => setFormData({ ...formData, polling_interval_min: parseInt(e.target.value) || 15 })}
                className="w-32"
                data-testid="polling-interval-input"
              />
            </div>

            {/* Import Start Date */}
            <div className="space-y-3">
              <Label>Импорт с даты</Label>
              <p className="text-sm text-gray-500">
                Импортировать задачи, созданные начиная с указанной даты
              </p>
              
              {/* Presets */}
              <div className="flex flex-wrap gap-2">
                {datePresets.map((preset) => (
                  <Button
                    key={preset.id}
                    type="button"
                    variant={selectedPreset === preset.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePresetChange(preset.id)}
                    data-testid={`preset-${preset.id}`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              
              {/* Date Picker */}
              <div className="flex items-center gap-4">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[240px] justify-start text-left font-normal"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.import_start_date 
                        ? format(formData.import_start_date, 'dd.MM.yyyy', { locale: ru })
                        : 'Выберите дату'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.import_start_date}
                      onSelect={handleDateChange}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ru}
                    />
                  </PopoverContent>
                </Popover>
                
                {formData.import_start_date && (
                  <span className="text-sm text-gray-500">
                    Будут импортированы задачи с {format(formData.import_start_date, 'd MMMM yyyy', { locale: ru })}
                  </span>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-settings-btn"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Сохранить настройки
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Warning Card */}
        {!settings.configured && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold">Важно</p>
                  <p>Перед сохранением убедитесь, что whitelist содержит корректные Bitrix User ID сотрудников вашего проектного контура.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="text-sm text-blue-800 space-y-2">
              <p className="font-semibold">ℹ️ Как получить Webhook URL:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Откройте ваш портал Bitrix24</li>
                <li>Перейдите в Приложения → Вебхуки → Добавить вебхук</li>
                <li>Выберите тип: Входящий вебхук</li>
                <li>Выберите права: tasks (чтение), user (чтение)</li>
                <li>Скопируйте сгенерированный URL</li>
              </ol>
              <p className="mt-3 font-semibold">ℹ️ Как найти Bitrix User ID:</p>
              <p className="ml-2">Откройте профиль сотрудника в Bitrix24. В адресной строке будет URL вида: <code className="bg-white px-1 rounded">/company/personal/user/2471/</code> — число 2471 это User ID.</p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BitrixIntegrationPage;
